import { API } from '../apis/apiWrapper';
import { Storage } from '../apis/s3Wrapper';
import { cloneDeep, get } from 'lodash';
import html2canvas from 'html2canvas';
import JSPdf from 'jspdf';

import { fetchAIForm } from 'src/apis';
import { defaultFieldProps, fullFieldProps, usStateOptions } from 'src/constants';
import { FormFieldType, ISummary, TAIForm, TAIFormMaterial, TCustomField, TRequest, IMaterial, TDropItem } from 'src/types';
import { processResponse } from './api';

import { formatDate, getJoinedDate, parseDate } from './day';
import { formatString } from './string';
import { downloadCSV } from './csv';
import { buildBatches } from 'src/services/schoolRequest';

const MARGIN = 10;

export const getUpdatedAIFormValues = (school: any): any => {
  const values = cloneDeep(school);
  const {
    schoolEstMonth,
    schoolEstDay,
    schoolEstYear,
    approvedAccreditation: { cbAccreditedMonth, cbAccreditedDay, cbAccreditedYear },
    administeredTest: { administeredTestMonth, administeredTestDay, administeredTestYear },
  } = school;

  values.schoolEstDate = getJoinedDate(schoolEstMonth, schoolEstDay, schoolEstYear) || parseDate(values.schoolEstDate);

  values.approvedAccreditation.cbAccreditedDate =
    getJoinedDate(cbAccreditedMonth, cbAccreditedDay, cbAccreditedYear) ||
    parseDate(values.approvedAccreditation.cbAccreditedDate);

  values.administeredTest.administeredTestDate =
    getJoinedDate(administeredTestMonth, administeredTestDay, administeredTestYear) ||
    parseDate(values.administeredTest.administeredTestDate || values.administeredTest.administeredtestDate);

  return values;
};

export const getValidatedSchoolName = (schoolName: string): any => {
  //replace greater than symbol ,less than symbol in school name with single space
  const schoolNameWithoutAngleBrackets = schoolName.replace(/[<>]+/g, ' ');

  // for single and double quotes in school name, we need to remove them and NOT replace with single space
  const schoolNameWithoutQuotes = schoolNameWithoutAngleBrackets.replace(/['"]+/g, '');

  return schoolNameWithoutQuotes;
};

export const getAIFormRequestData = (formData: any, userName: string, userEmail: string): any => {
  try {
    const accrExpirationDate = new Date(formData?.approvedAccreditation?.cbAccreditedDate);
    const today = new Date(Date.now());
    const numDaysDiff = Math.ceil((accrExpirationDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
    if (numDaysDiff < 31) {
      formData.accreditationExpiry = 'Y';
    } else {
      formData.accreditationExpiry = 'N';
    }
  } catch (e) {
    formData.accreditationExpiry = 'Y';
  }

  formData.creator = userName;
  formData.creatorEmail = userEmail;

  if (formData.processStatus === 'reopen') {
    formData.processStatus = 'processing';
  } else {
    formData.processStatus = 'submitted';
  }
  return formData;
};

interface ISummaryPK {
  categoryId: string;
}
export const uploadSchoolFile = async (user: string, school: ISummary | TAIForm, file: File, description: string, path?: string) => {
  const contentType = file.type;
  let fileName = file.name;

  if (!contentType || (contentType !== 'application/pdf' && !contentType.startsWith('image'))) {
    throw new Error('You can only upload images or PDF files.');
  }

  fileName = fileName.replace(/[\s,//]+/g, '');
  if (fileName.length > 200) {
    fileName = fileName.substr(0, 200);
  }

  try {
    const result = await Storage.put(school.categoryId + '/support/' + fileName, file, {
      level: 'private',
      contentType: contentType,
    });

    await API.post('AiFormRequest', '/save?key=support', {
      body: {
        ...school,
        user: user,
        s3key: result.key,
        fieldPath: path,
        fileName,
        contentType,
        description,
      },
    }).then((response: any) => processResponse(response));

    return result.key;
  } catch (error: any) {
    throw new Error('Failed to upload file: ' + error.message);
  }
};

export const getFileLink = async (data: IMaterial) => {
  return await API.post('AiFormRequest', '/save?key=getFileLink', {
    body: {
      filePath: data.filePath
    },
  }).then((response: any) => processResponse(response));
};

export const getRuntimeConfigFromSever = async () => {
  return await API.post('AiFormRequest', '/save?key=getRuntimeConfig').then((response: any) => processResponse(response));
};

export const uploadFile = async (user: string, school: ISummaryPK, file: File, description?: string, path?: string, multi?: boolean): Promise<IMaterial> => {
  const contentType = file.type;
  let fileName = file.name;

  if (!contentType || (contentType !== 'application/pdf' && !contentType.startsWith('image'))) {
    throw new Error('You can only upload images or PDF files.');
  }

  fileName = fileName.replace(/[\s,//]+/g, '');
  if (fileName.length > 200) {
    fileName = fileName.substr(0, 200);
  }

  try {
    const result = await Storage.put(school.categoryId + '/support/' + fileName, file, {
      level: 'private',
      contentType: contentType,
    });

    const record: IMaterial = await API.post('AiFormRequest', '/save?key=support', {
      body: {
        ...school,
        user: user,
        s3key: result.key,
        fieldPath: path,
        fileName,
        contentType,
        description,
        multi
      },
    }).then((response: any) => processResponse(response));

    return record;
  } catch (error: any) {
    const type = typeof error;
    let message = 'Unknown error occurred uploading the file';
    if (type === 'object') {
      message = error.message || message;
    } else if (type === 'string') {
      message = error;
    } else {
      console.log(error);
    }
    throw new Error(`Failed to upload file: ${message}`);
  }
};

export const generatePDF = async (printSections: HTMLDivElement[], schoolName: string) => {
  const results = await Promise.all(
    printSections.map(async (section) => {
      const canvas = await html2canvas(section, { logging: false });
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      return {
        imageData: canvas,
        width: imgWidth,
        height: imgHeight,
      };
    }),
  );

  const doc = new JSPdf();
  const fileName = schoolName + '.pdf';
  let pageY = MARGIN;

  results.forEach((image) => {
    if (!image.height || !image.width) {
      return;
    }

    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();

    if (pageY + image.height > pageHeight - MARGIN) {
      pageY = MARGIN;
      if (image.height <= pageHeight) {
        doc.addPage();
      }
    }

    doc.addImage(image.imageData, 'PNG', MARGIN, pageY, pageWidth - 2 * MARGIN, image.height);
    pageY += image.height;
  });

  doc.save(fileName);
};

export const formatProcessStatus = (status: TAIForm['processStatus']) => {
  return {
    started: 'Started',
    submitted: 'Submitted',
    reopen: 'Reopened',
    processing: 'Under Review',
    qcReadyVetting: 'QC Vetting Approved',
    qcReadyReopen: 'QC Reopened',
    qcReadyRejected: 'QC Rejected',
    completed: 'Completed',
    appeal: 'Pending Appeal',
    deactivationApproval: 'Pending Deactivation Approval',
    international: 'Pending International',
    testSecurity: 'Pending Test Security',
    opsApproved: 'Vetting Approved',
    rejected: 'Rejected',
    xpmDenied: 'XPM Denied'
  }[status];
};

function getDate(dateTime:any){
  return `${dateTime.getFullYear()}-${dateTime.getMonth() + 1}-${dateTime.getDate()}`
}

/**
 * Generate a CSV file for Client AI Requests.
 */
export const buildAiCsv = async () => {

  const today = new Date() ;
  today.setDate(today.getDate() + 1); // Get the next day

  const lastYear = new Date();
  lastYear.setFullYear(lastYear.getFullYear() - 1);

  console.log('startDate = ' + getDate(lastYear) + '; endDate = ' + getDate(today));

  const summaries : any = await API.post('AiFormRequest', '/save?key=retrieveSubmissionRequests', {
      body :{
        recordCategoryType : 'ai',
        startDate : getDate(lastYear), // Format : '2023-05-10'
        endDate : getDate(today) // Format : '2023-10-25'
  },
  }).then((response: any) => processResponse(response));

  let mapped = summaries.map((summary:any) => {

    //  Note : *** DEX Requests do not have submittedDate ***
    if (!summary.submittedDate /*|| !summary.submissionDate*/) {
      return null;
    }

    return {
      'Initial Submit Date': formatDate(summary.submittedDate),
      'Last Submit Date': formatDate(summary.submissionDate),
      'Last Update Date': formatDate(summary.updateDate),
      'Submitter': summary.submitter,
      'Username': summary.username,
      'Email': summary.email,
      'School Name': summary.schoolName,
      'Action': summary.action,
      'Process Status': formatProcessStatus(summary.processStatus),
      'CountryIsoCd': summary.visitingAddress?.countryIsoCd ?? '',
      'City': summary.visitingAddress?.city ?? '',
      'StateCd': summary.visitingAddress?.stateCd ?? '',
      'Province': summary.visitingAddress?.province ?? '',
      'Assigned SC': summary.aiCode ?? '',
      //'Material List': convertMaterialList(summary.materials),
      'Applied Type': summary.appliedType ?? '',
      'Applied Level': summary.appliedLevel ?? '',
      //'Applied Grade Level': summary.schoolType === 'hs' ? 'High School' : 'MiddleSchool', //Maybe we need to move this too to dynamo
      'Assigned AI Level': summary.aiLevel ?? '',
      'Interested in AP Courses': summary.apCourseOffering?.apCourseOffer,
      'AP Courses Interested in': summary.apCourseOffering?.apCourseOfferList,
      'Interested in SAT Test Center' : summary.collegeBoardOfferings?.satTestCenter,
      'SAT Admin Date': summary.collegeBoardOfferings?.satAdminDate ?? '',
      'Interested in Pre-AP' : summary.collegeBoardOfferings?.preApCourses,
      'Interested in PSAT' : summary.collegeBoardOfferings?.administerPsat,

      'NMI': summary.reOpen,
      'XPM Denied': summary.xpmDenied,
      'Days In Current Status': summary.daysInCurrentStatus,
      'Days Since Initial Submit': summary.daysSinceInitialSubmit,
      'Days Since Last Submit': summary.daysSinceLastSubmit,

      // For Debugging
      //'UserId': summary.userId,
      //'CategoryId': summary.categoryId,
    };
  });

  const csvHeaders = [
    'Initial Submit Date',
    'Last Submit Date',
    'Last Update Date',
    'Submitter',
    'Username',
    'Email',
    'School Name',
    'Action',
    'Process Status',
    'CountryIsoCd',
    'City',
    'StateCd',
    'Province',
    'Applied Type',
    'Applied Level',
    //'Applied Grade Level',
    'Assigned SC',
    'Assigned AI Level',
    //'Material List',
    'Interested in AP Courses',
    'AP Courses Interested in',
    'Interested in SAT Test Center',
    'SAT Admin Date',
    'Interested in Pre-AP',
    'Interested in PSAT',

    'NMI',
    'XPM Denied',
    'Days In Current Status',
    'Days Since Initial Submit',
    'Days Since Last Submit',

    // For Debugging
    //'UserId',
    //'CategoryId'
  ];


  // Get rid of empty objects
  mapped = mapped.filter((each:any) => each);

  // Sort by Initial Submit Date
  mapped.sort(function(a:any, b:any) {
    const aDate = new Date(a['Last Submit Date']);
    const bDate = new Date(b['Last Submit Date']);
    return aDate.getTime() - bDate.getTime();
  });

  const csvData = mapped
      .filter((each:any) => each)
      .map((row:any) => csvHeaders.map((fieldName) => {
        const value: string = get(row, fieldName, '');
        if (typeof value === 'string') {
          return formatString(value);
        } else if (typeof value === 'number') {
          return value;
        } else {
          console.log(`Field wasn't a string: '${fieldName}'`, value);
          return '';
        }
      }).join(','))
      .join('\n');

  downloadCSV({
    headers: csvHeaders,
    data: csvData,
    fileName: 'SchoolCode_Requests.csv',
  });
};


/**
 * Generate a CSV file for Client AI Requests.
 */
export const buildDiCsv = async () => {

  const today = new Date() ;
  today.setDate(today.getDate() + 1); // Get the next day

  const lastYear = new Date();
  lastYear.setFullYear(lastYear.getFullYear() - 1);

  console.log('startDate = ' + getDate(lastYear) + '; endDate = ' + getDate(today));

  const summaries : any = await API.post('AiFormRequest', '/save?key=retrieveSubmissionRequests', {
    body :{
      recordCategoryType : 'di',
      startDate : getDate(lastYear), // Format : '2023-05-10'
      endDate : getDate(today) // Format : '2023-10-25'
    },
  }).then((response: any) => processResponse(response));

  let mapped = summaries.map((summary:any) => {

    //  Note : *** DEX Requests do not have submittedDate ***
    if (!summary.submittedDate /*|| !summary.submissionDate*/) {
      return null;
    }

    return {
      'Initial Submit Date': formatDate(summary.submittedDate),
      'Last Submit Date': formatDate(summary.submissionDate),
      'Last Update Date': formatDate(summary.updateDate),
      'Submitter': summary.submitter,
      'Username': summary.username,
      'Email': summary.email,
      'Institution Name': summary.schoolName,
      'Action': summary.action,
      'Process Status': formatProcessStatus(summary.processStatus),
      'CountryIsoCd': summary.visitingAddress?.countryIsoCd ?? '',
      'City': summary.visitingAddress?.city ?? '',
      'StateCd': summary.visitingAddress?.stateCd ?? '',
      'Province': summary.visitingAddress?.province ?? '',
      'Assigned SSC': summary.diCode ?? '',
      'SAT Program Accepted' : summary.satEligibilityInd,
      'AP Program Accepted' : summary.apEligibilityInd,

      'NMI': summary.reOpen,
      'XPM Denied': summary.xpmDenied,
      'Days In Current Status': summary.daysInCurrentStatus,
      'Days Since Initial Submit': summary.daysSinceInitialSubmit,
      'Days Since Last Submit': summary.daysSinceLastSubmit,

      // For Debugging
      //'UserId': summary.userId,
      //'CategoryId': summary.categoryId,
    };
  });

  const csvHeaders = [
    'Initial Submit Date',
    'Last Submit Date',
    'Last Update Date',
    'Submitter',
    'Username',
    'Email',
    'Institution Name',
    'Action',
    'Process Status',
    'CountryIsoCd',
    'City',
    'StateCd',
    'Province',
    'Assigned SSC',
    'SAT Program Accepted',
    'AP Program Accepted',

    'NMI',
    'XPM Denied',
    'Days In Current Status',
    'Days Since Initial Submit',
    'Days Since Last Submit',

    // For Debugging
    //'UserId',
    //'CategoryId'
  ];


  // Get rid of empty objects
  mapped = mapped.filter((each:any) => each);

  // Sort by Initial Submit Date
  mapped.sort(function(a:any, b:any) {
    const aDate = new Date(a['Last Submit Date']);
    const bDate = new Date(b['Last Submit Date']);
    return aDate.getTime() - bDate.getTime();
  });

  const csvData = mapped
      .filter((each:any) => each)
      .map((row:any) => csvHeaders.map((fieldName) => {
        const value: string = get(row, fieldName, '');
        if (typeof value === 'string') {
          return formatString(value);
        } else if (typeof value === 'number') {
          return value;
        } else {
          console.log(`Field wasn't a string: '${fieldName}'`, value);
          return '';
        }
      }).join(','))
      .join('\n');

  downloadCSV({
    headers: csvHeaders,
    data: csvData,
    fileName: 'ScoreSendCode_Requests.csv',
  });
};

export const getAddressFields = (prefix: string, countries: TDropItem[]): TCustomField[] => {
  return [
    {
      label: 'Address 1:',
      comment: 'P.O. Box addresses are not acceptable',
      path: `${prefix}.address1`,
      placeholder: 'Street Address',
      type: FormFieldType.TEXT,
      required: true,
      ...fullFieldProps,
    },
    {
      label: 'Address 2:',
      path: `${prefix}.address2`,
      placeholder: 'Street Address',
      type: FormFieldType.TEXT,
      ...fullFieldProps,
    },
    {
      label: 'Address 3:',
      path: `${prefix}.address3`,
      placeholder: 'Street Address',
      type: FormFieldType.TEXT,
      ...fullFieldProps,
    },
    {
      label: 'City:',
      path: `${prefix}.city`,
      placeholder: 'City',
      type: FormFieldType.TEXT,
      required: true,
      ...defaultFieldProps,
    },
    {
      label: 'County:',
      path: `${prefix}.county`,
      placeholder: 'County',
      type: FormFieldType.TEXT,
      ...defaultFieldProps,
      renderCondition: (field) => field[prefix]?.country === 'US' || field[prefix]?.country === '',
    },
    {
      label: 'State/Province:',
      path: `${prefix}.stateOrProvince`,
      placeholder: 'State/Province',
      type: FormFieldType.TEXT,
      ...defaultFieldProps,
      renderCondition: (field) => field[prefix]?.country !== 'US' && field[prefix]?.country !== '',
    },
    {
      label: 'Country:',
      path: `${prefix}.country`,
      placeholder: 'Select One',
      type: FormFieldType.SELECT,
      options: countries,
      required: true,
      ...defaultFieldProps,
    },
    {
      label: 'State:',
      path: `${prefix}.state`,
      placeholder: 'Select One',
      type: FormFieldType.SELECT,
      options: usStateOptions,
      ...defaultFieldProps,
      required: true,
      renderCondition: (field) => field[prefix]?.country === 'US' || field[prefix]?.country === '',
    },
    {
      label: 'Zip:',
      maxLength: 5,
      path: `${prefix}.zip`,
      placeholder: '5 Digit Zip Code',
      type: FormFieldType.TEXT,
      required: true,
      ...defaultFieldProps,
      renderCondition: (field) => field[prefix]?.country === 'US' || field[prefix]?.country === '',
    },
    {
      label: 'International Postal Code:',
      path: `${prefix}.internationalPostalCode`,
      placeholder: 'International Postal Code',
      type: FormFieldType.TEXT,
      ...defaultFieldProps,
      renderCondition: (field) => field[prefix]?.country !== 'US' && field[prefix]?.country !== '',
    },
  ];
};
